import React, { useEffect, useState } from "react";
import { Card } from "@blueprintjs/core";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { DefaultOption } from "../../../../translations/experiments/Default";
import { colors, commonStyles } from "../../../../components/colors";

const barColors = [colors.lichtBlau, colors.darkGreen]; // For group bar charts
const barColorsContrast = [colors.lichtBlau, colors.sienna]; // For overall percentage bar

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 350,
    maxWidth: 400,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 250
};

const summaryMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 800,
    marginLeft: "auto",
    marginRight: "auto",
    padding: "16px 24px"
};

const overallCardStyle: React.CSSProperties = {
    background: colors.white,
    maxWidth: 800,
    width: "100%",
    margin: "32px auto 0 auto",
    padding: "28px 32px",
    borderRadius: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
};

export const CrossoverKey = "CrossoverDefault";

function getGroupFromDefaultOption(cookieDefault: number) {
    if (cookieDefault === 0) return 1;
    if (cookieDefault === 1) return 2;
    return null;
}

function getUserCookieSelection(userData: any) {
    if (userData && userData[CrossoverKey]) {
        for (const entry of Object.values(userData[CrossoverKey])) {
            if (
                entry &&
                typeof entry === "object" &&
                Array.isArray((entry as any).selections) &&
                (entry as any).selections.length > 0
            ) {
                const selection = (entry as any).selections[0];
                if (selection && (selection.defaultOption === 0 || selection.defaultOption === 1)) {
                    return selection;
                }
            }
        }
    }
    return null;
}

function getUserCookieSummary(selection: any, t: any) {
    if (!selection) return {};
    const groupNum = getGroupFromDefaultOption(selection.defaultOption);
    const voreinstellung =
        selection.defaultOption === 0
            ? t.cookiesAccept
            : t.cookiesDecline;
    const entscheidung =
        selection.choice === 0
            ? t.cookiesAccept
            : t.cookiesDecline;
    const alignment = selection.choice === selection.defaultOption
        ? t.defaultChosen
        : t.againstDefault;
    return { groupNum, voreinstellung, entscheidung, alignment };
}

export const ResultScreenCrossoverCookiesComponent: React.FC = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    // translations for this screen
    const t = {
        ...DefaultOption[language].results.cookies, // <-- fixed path
        cookiesAccept: DefaultOption[language].screens.screen1.cookieBanner.options[0].label,
        cookiesDecline: DefaultOption[language].screens.screen1.cookieBanner.options[1].label,
        defaultChosen: DefaultOption[language].results.cookies.defaultChosen,
        againstDefault: DefaultOption[language].results.cookies.againstDefault,
    };

    const [group1Counts, setGroup1Counts] = useState([0, 0]); // [accept, decline]
    const [group2Counts, setGroup2Counts] = useState([0, 0]);
    const [totalAligned, setTotalAligned] = useState(0);
    const [totalMisaligned, setTotalMisaligned] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    // Find user's cookie selection (by defaultOption)
    const userCookieSelection = getUserCookieSelection(userData);
    const summary = getUserCookieSummary(userCookieSelection, t);

    useEffect(() => {
        setLoading(true);
        getGroupData().then((data: any[]) => {
            let grp1Accept = 0, grp1Decline = 0, grp2Accept = 0, grp2Decline = 0;
            let aligned = 0, misaligned = 0;
            let sum = 0;

            data?.forEach((dat: any) => {
                const selection = getUserCookieSelection(dat);
                if (selection) {
                    sum++;
                    const group = getGroupFromDefaultOption(selection.defaultOption);
                    if (selection.choice === 0) {
                        group === 1 ? grp1Accept++ : grp2Accept++;
                    } else if (selection.choice === 1) {
                        group === 1 ? grp1Decline++ : grp2Decline++;
                    }
                    if (selection.choice === selection.defaultOption) {
                        aligned++;
                    } else {
                        misaligned++;
                    }
                }
            });

            setGroup1Counts([grp1Accept, grp1Decline]);
            setGroup2Counts([grp2Accept, grp2Decline]);
            setTotalAligned(aligned);
            setTotalMisaligned(misaligned);
            setTotal(sum);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [getGroupData]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>{t.loading}</div>;
    }

    // Convert to percentage
    const alignedPercent = total > 0 ? Math.round((totalAligned / total) * 100) : 0;
    const misalignedPercent = total > 0 ? Math.round((totalMisaligned / total) * 100) : 0;
    const relativeData = [
        { name: t.defaultChosen, value: alignedPercent },
        { name: t.againstDefault, value: misalignedPercent }
    ];

    // Styling for highlight (border color)
    const highlightStyle = {
        border: `2px solid ${colors.brillantBlue}`,
    };

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t.title}
            </h2>
            <div style={{
                display: "flex",
                gap: 24,
                marginTop: 32,
                flexWrap: "wrap",
                justifyContent: "center"
            }}>
                <Card
                    style={{
                        ...cardStyle,
                        ...(summary.groupNum === 1 ? highlightStyle : {})
                    }}
                >
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>{t.group1.title}</h3>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart
                            data={[
                                { name: t.cookiesAccept, value: group1Counts[0] },
                                { name: t.cookiesDecline, value: group1Counts[1] }
                            ]}
                            barCategoryGap={40}
                        >
                            <XAxis dataKey="name" interval={0} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="value" barSize={36}>
                                <Cell fill={barColors[0]} />
                                <Cell fill={barColors[1]} />
                                <LabelList dataKey="value" position="top" />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
                <Card
                    style={{
                        ...cardStyle,
                        ...(summary.groupNum === 2 ? highlightStyle : {})
                    }}
                >
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>{t.group2.title}</h3>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart
                            data={[
                                { name: t.cookiesAccept, value: group2Counts[0] },
                                { name: t.cookiesDecline, value: group2Counts[1] }
                            ]}
                            barCategoryGap={40}
                        >
                            <XAxis dataKey="name" interval={0} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="value" barSize={36}>
                                <Cell fill={barColors[0]} />
                                <Cell fill={barColors[1]} />
                                <LabelList dataKey="value" position="top" />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            </div>
            <div style={summaryMessageStyle}>
                {summary.groupNum && (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t.summary
                                .replace("{group}", String(summary.groupNum))
                                .replace("{default}", summary.voreinstellung)
                                .replace("{choice}", summary.entscheidung)
                                .replace("{alignment}", summary.alignment)
                        }}
                    />
                )}
            </div>
            <div style={{
                marginTop: 32,
                textAlign: "center",
                fontSize: 18,
                maxWidth: 800,
                width: "100%",
                marginLeft: "auto",
                marginRight: "auto"
            }}>
                <Card style={overallCardStyle}>
                    <h4 style={{
                        marginBottom: 24,
                        color: colors.brillantBlue,
                        fontWeight: 600,
                        fontSize: 20
                    }}>
                        {t.overall}
                    </h4>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart
                            data={relativeData}
                            barCategoryGap={60}
                        >
                            <XAxis
                                dataKey="name"
                                interval={0}
                                angle={0}
                                textAnchor="middle"
                                tick={{ fontSize: 16 }}
                            />
                            <YAxis
                                domain={[0, 100]}
                                allowDecimals={false}
                                tickFormatter={(value) => `${value}%`}
                            />
                            <Tooltip formatter={(value: any) => `${value}%`} />
                            <Bar dataKey="value" barSize={56}>
                                <Cell fill={barColorsContrast[0]} />
                                <Cell fill={barColorsContrast[1]} />
                                <LabelList dataKey="value" position="top" formatter={(value: any) => `${value}%`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            </div>
        </div>
    );
};

export const ResultScreenCrossoverCookies: ScreenConfig = {
    component: ResultScreenCrossoverCookiesComponent,
    defaultResult: {
        selections: [],
    },
    canContinueDefault: true,
};